/* SanitairLuxe — ENIGE regeling voor variantfoto's op de productpagina (2026-10-01).
   - De server (sections/main-product.liquid) zet de juiste foto's al klaar (variant_<id> groepen).
   - Bij een variantwissel roept product.js (VariantSelects.updateMedia) SLVariantMedia.handle() aan.
   - Ongeldige combinaties (bv. Mat zwart + Standaard) in de 2e/3e keuzelijst verbergen en vóór
     product.js stil corrigeren naar een geldige uitvoering.
   - data-group kan een LIJST zijn ("variant_A variant_B").
   - SL 2026-10-05: een keuzeveld (fieldset) met maar één mogelijke waarde wordt verborgen
     (bv. Afmeting met alleen "1700x800x580", of Uitvoering waar na de kleurkeuze maar één
     uitvoering overblijft). De keuze zelf blijft geselecteerd. Tijdelijke ?sldebug-diagnose verwijderd.
   - SL 2026-10-05 (c): foto's met een optiegroep (kleur_x, aantal-kraangaten_0, ...) blijven zichtbaar
     als de gekozen variant die optiewaarde heeft.
   Backup vorige versie: assets/bak-20261001h-stonea-variant-media-v2.js */
(() => {
  'use strict';
  if (window.SLVariantMedia) return;

  const SLIDE = '.product-images__slide';
  const THUMB = '.product-thumbnail';
  const PICKERS = 'variant-selects, variant-radios';

  const mediaIdOf = (el) => {
    const match = ((el && el.id) || '').match(/-(\d+)$/);
    return match ? match[1] : null;
  };

  const orderOf = (el) => {
    const value = Number(el.dataset.slOrder);
    return Number.isFinite(value) ? value : 0;
  };

  const stampOrder = (slider) => {
    slider.querySelectorAll(SLIDE).forEach((slide, index) => {
      if (slide.dataset.slOrder === undefined) slide.dataset.slOrder = String(index);
    });
  };

  const optionsOf = (variant) => {
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
  };

  const getVariants = (picker) => {
    if (picker._slVariants) return picker._slVariants;
    let list = [];
    const node = picker.querySelector('script[type="application/json"]');
    if (node) {
      try {
        const data = JSON.parse(node.textContent);
        if (Array.isArray(data)) list = data;
        else if (data && Array.isArray(data.variants)) list = data.variants;
      } catch (error) {}
    }
    picker._slVariants = list;
    return list;
  };

  const getControls = (picker) => [...picker.querySelectorAll('fieldset')].map((fieldset) => ({
    fieldset,
    radios: [...fieldset.querySelectorAll('input[type="radio"]')],
    select: fieldset.querySelector('select'),
  }));

  const getSelected = (picker) => getControls(picker).map(({ radios, select }) => {
    const checked = radios.find((radio) => radio.checked);
    if (checked) return checked.value;
    return select ? select.value : null;
  });

  const findVariant = (variants, selected) => variants.find((variant) => {
    const options = optionsOf(variant);
    return options.length === selected.length && options.every((value, index) => value === selected[index]);
  });

  const resolveVariant = (picker) => {
    if (picker.currentVariant) return picker.currentVariant;
    return findVariant(getVariants(picker), getSelected(picker)) || null;
  };

  const unveil = (scope) => {
    const loader = window.lazySizes && window.lazySizes.loader;
    if (!loader || typeof loader.unveil !== 'function') return;
    scope.querySelectorAll('img.lazyload').forEach((img) => loader.unveil(img));
  };

  const apply = (root, variant) => {
    if (!root || !variant) return false;
    const slider = root.querySelector('product-slider.product-images') || root.querySelector('.product-images');
    if (!slider) return false;

    stampOrder(slider);
    const slides = [...slider.querySelectorAll(SLIDE)].sort((a, b) => orderOf(a) - orderOf(b));
    const isVariantSet = (el) => el.dataset.setName === 'variant';
    if (!slides.some(isVariantSet)) return false;

    const group = `variant_${variant.id}`;
    const inGroup = (el) => (' ' + (el.dataset.group || '') + ' ').indexOf(' ' + group + ' ') !== -1;
    const hasGroup = slides.some((slide) => isVariantSet(slide) && inGroup(slide));
    /* SL 2026-10-05 (c): foto's met een OPTIEGROEP (bv. data-set-name="maat", data-group="maat_60-cm")
       horen bij elke variant met die optiewaarde. Voorheen werden ze verborgen zodra de variant ook eigen
       variantfoto's had (bv. maattekening Aloni Sally 60 cm, nisdeur links/rechts bij Wiesbaden Maki). */
    const handleize = (value) => String(value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const variantValues = optionsOf(variant).map(handleize);
    const matchesOptionGroup = (slide) => {
      const set = slide.dataset.setName || '';
      const value = (slide.dataset.group || '').slice(set.length + 1);
      /* alleen echte woordwaarden (bv. kleur); korte cijfers als "0" of "1" matchen te vaak per ongeluk */
      return !!set && value.length >= 3 && /[a-z]/.test(value) && variantValues.includes(value);
    };
    const isVisible = (slide) => {
      if (!slide.dataset.group) return true;
      if (!isVariantSet(slide) && matchesOptionGroup(slide)) return true;
      if (hasGroup) return inGroup(slide);
      return isVariantSet(slide);
    };

    const visibleSlides = slides.filter(isVisible);
    if (!visibleSlides.length) return false;
    const firstSlide = visibleSlides[0];
    const visibleIds = new Set(visibleSlides.map(mediaIdOf));
    const firstId = mediaIdOf(firstSlide);

    slides.forEach((slide) => {
      slide.classList.toggle('is-active', visibleIds.has(mediaIdOf(slide)));
      slide.classList.toggle('is-initial-selected', slide === firstSlide);
    });

    const thumbWrap = root.querySelector('.product-thumbnail-container');
    if (thumbWrap) {
      thumbWrap.querySelectorAll(THUMB).forEach((thumb) => {
        const id = mediaIdOf(thumb);
        const selected = id === firstId;
        thumb.classList.toggle('is-active', visibleIds.has(id));
        thumb.classList.toggle('is-initial-selected', selected);
        thumb.classList.toggle('sl-thumb-active', selected);
      });
      thumbWrap.scrollLeft = 0;
      thumbWrap.scrollTop = 0;
    }

    const flkty = slider.flkty;
    if (flkty && typeof slider.reInit === 'function') {
      const cells = flkty.cells.map((cell) => cell.element);
      const sameCells = cells.length === visibleSlides.length && cells.every((el, index) => el === visibleSlides[index]);
      if (!sameCells) slider.reInit();
      const current = slider.flkty;
      if (current) {
        if (current.selectedElement !== firstSlide) current.selectCell(firstSlide, false, true);
        slider.selectedIndex = current.selectedIndex;
        current.resize();
        const cellSet = new Set(current.cells.map((cell) => cell.element));
        slides.forEach((slide) => {
          if (!cellSet.has(slide)) slide.classList.remove('is-selected');
        });
      }
    }

    unveil(firstSlide);
    visibleSlides.slice(1).forEach(unveil);
    if (thumbWrap) thumbWrap.querySelectorAll(`${THUMB}.is-active`).forEach(unveil);
    return true;
  };

  const setControlValueSilently = (control, value) => {
    const radio = control.radios.find((item) => item.value === value);
    if (radio) {
      if (radio.checked) return false;
      radio.checked = true;
      return true;
    }
    if (control.select && control.select.value !== value) {
      control.select.value = value;
      return control.select.value === value;
    }
    return false;
  };

  const toggleFieldset = (fieldset, hide) => {
    if (!fieldset) return;
    fieldset.hidden = hide;
    fieldset.style.display = hide ? 'none' : '';
    fieldset.toggleAttribute('data-sl-single-option', hide);
  };

  const syncOptionAvailability = (picker) => {
    const variants = getVariants(picker);
    const controls = getControls(picker);
    if (!variants.length || !controls.length) return false;

    const selected = getSelected(picker);
    let corrected = false;

    controls.forEach((control, index) => {
      const validValues = new Set();
      variants.forEach((variant) => {
        const options = optionsOf(variant);
        const matchesPrevious = selected.slice(0, index).every((value, optionIndex) => !value || options[optionIndex] === value);
        if (matchesPrevious && options[index]) validValues.add(options[index]);
      });
      if (!validValues.size) return;

      toggleFieldset(control.fieldset, validValues.size <= 1);
      if (index === 0) return;

      if (control.select) {
        [...control.select.options].forEach((option) => {
          const valid = validValues.has(option.value);
          option.hidden = !valid;
          option.disabled = !valid;
          option.style.display = valid ? '' : 'none';
          option.toggleAttribute('data-stonea-unavailable', !valid);
        });
      }
      control.radios.forEach((radio) => {
        const valid = validValues.has(radio.value);
        const wrapper = radio.closest('label, .product-form__input, .form__label') || radio.parentElement;
        radio.disabled = !valid;
        radio.hidden = !valid;
        if (wrapper && wrapper !== control.fieldset) wrapper.hidden = !valid;
      });

      if (selected[index] && !validValues.has(selected[index])) {
        const nextValue = [...validValues][0];
        if (setControlValueSilently(control, nextValue)) {
          selected[index] = nextValue;
          corrected = true;
        }
      }
    });

    return corrected;
  };

  const handlePicker = (picker) => {
    if (!picker) return false;
    return apply(picker.closest('.thb-product-detail'), resolveVariant(picker));
  };

  const syncAll = () => {
    document.querySelectorAll(PICKERS).forEach((picker) => {
      const corrected = syncOptionAvailability(picker);
      if (corrected && picker.currentVariant !== undefined) {
        const control = picker.querySelector('select, input[type="radio"]:checked');
        if (control) control.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      handlePicker(picker);
    });
  };

  window.SLVariantMedia = { handle: handlePicker, apply, sync: syncAll };
  window.slGalleryApply = syncAll;

  document.addEventListener('change', (event) => {
    const picker = event.target && event.target.closest && event.target.closest(PICKERS);
    if (picker) syncOptionAvailability(picker);
  }, true);

  document.addEventListener('shopify:section:load', syncAll);
  window.addEventListener('pageshow', (event) => { if (event.persisted) syncAll(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll, { once: true });
  } else {
    syncAll();
  }
})();

/* SL 2026-10-01 (e): THUMBNAIL-STRIP NIET MEER HALF AFGESNEDEN.
   Alleen voor een horizontaal scrollende .product-thumbnail-container: niet centreren, alleen schuiven
   als de actieve thumbnail buiten beeld valt, uitgelijnd op het begin van een hele thumbnail. */
(() => {
  'use strict';
  const nativeScrollTo = Element.prototype.scrollTo;

  const patch = (box) => {
    if (!box || box._slThumbScrollPatched) return;
    box._slThumbScrollPatched = true;

    box.scrollTo = function (arg, y) {
      const horizontal = this.scrollWidth > this.clientWidth + 1 && this.scrollHeight <= this.clientHeight + 1;
      const active = this.querySelector('.product-thumbnail.is-initial-selected');
      if (!horizontal || !active || !arg || typeof arg !== 'object') {
        return nativeScrollTo.apply(this, arguments);
      }

      const boxLeft = this.getBoundingClientRect().left;
      const posOf = (el) => el.getBoundingClientRect().left - boxLeft + this.scrollLeft;
      const activeWidth = active.getBoundingClientRect().width;
      if (!activeWidth) return nativeScrollTo.apply(this, arguments);

      const start = posOf(active);
      const end = start + activeWidth;
      const view = this.clientWidth;
      const max = Math.max(0, this.scrollWidth - view);

      let left = this.scrollLeft;
      if (start < left) {
        left = start;
      } else if (end > left + view) {
        left = end - view;
      } else {
        return;
      }

      const starts = [...this.querySelectorAll('.product-thumbnail')]
        .filter((thumb) => thumb.getBoundingClientRect().width > 0)
        .map(posOf)
        .sort((a, b) => a - b);
      const snapped = starts.find((value) => value >= left - 1);
      if (snapped !== undefined && snapped <= start) left = snapped;

      left = Math.max(0, Math.min(max, Math.round(left)));
      nativeScrollTo.call(this, { left, top: this.scrollTop, behavior: arg.behavior || 'smooth' });
    };
  };

  const run = () => document.querySelectorAll('.product-thumbnail-container').forEach(patch);

  document.addEventListener('shopify:section:load', run);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
